import { useState, useEffect } from "react";
import DSBox from "views/system-design/DSBox";
import DSBaseTable, {
  Column,
} from "views/system-design/Table/DSBaseTable/components/DSBaseTable";
import DSTableCell from "views/system-design/Table/DSTableCell";
import DSTextField from "views/system-design/DSTextField";
import DSButton from "views/system-design/DSButton";
import DSIconButton from "views/system-design/DSIconButton";
import DSTypography from "views/system-design/DSTypography";
import DSCircularProgress from "views/system-design/DSCircularProgress";
import DSAlert from "views/system-design/DSAlert";
import DSCollapsibleText from "views/system-design/DSCollapsibleText";
import DSChip from "views/system-design/DSChip";
import DeleteIcon from "@mui/icons-material/Delete";
import RestoreFromTrashIcon from "@mui/icons-material/RestoreFromTrash";
import firebaseService from "firebase_service/firebaseService";
import { IgnoredClockError } from "api/admin";
import {
  useIgnoredClockErrors,
  useCreateIgnoredClockError,
  useSoftDeleteIgnoredClockError,
  useRestoreIgnoredClockError,
} from "../hooks/useIgnoredClockErrors";

const columns: Column<IgnoredClockError>[] = [
  { field: "errorMessage", label: "Error Message" },
  { field: "method", label: "Method" },
  { field: "class", label: "Class" },
  { field: "clockId", label: "Clock ID" },
  { field: "createdBy", label: "Created By" },
  { field: "createdAt", label: "Created At" },
  { field: "deletedAt", label: "Status" },
];

const IgnoredErrorsTab = () => {
  const [version, setVersion] = useState<number | null>(
    firebaseService.general?.public ?? null,
  );
  const [versionInput, setVersionInput] = useState<string>(
    String(firebaseService.general?.public ?? ""),
  );

  useEffect(() => {
    if (version != null) return;
    const unsubscribe = firebaseService.db
      .collection("general")
      .doc("config")
      .onSnapshot((snap) => {
        const data = snap.data();
        if (version == null && data?.public) {
          setVersion(data.public);
          setVersionInput(String(data.public));
        }
      });
    return unsubscribe;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { data, isLoading } = useIgnoredClockErrors(version);
  const {
    mutate: createIgnored,
    isPending: isCreating,
    error: createError,
    reset: resetCreateError,
  } = useCreateIgnoredClockError();
  const { mutate: softDelete } = useSoftDeleteIgnoredClockError();
  const { mutate: restore } = useRestoreIgnoredClockError();

  const [newErrorMessage, setNewErrorMessage] = useState("");
  const [newMethod, setNewMethod] = useState("");
  const [newClass, setNewClass] = useState("");
  const [newClockId, setNewClockId] = useState("");

  const handleVersionChange = () => {
    const parsed = Number(versionInput);
    if (Number.isFinite(parsed) && parsed > 0) {
      setVersion(parsed);
    }
  };

  const handleCreate = () => {
    if (!version || !newErrorMessage.trim()) return;
    createIgnored(
      {
        errorMessage: newErrorMessage.trim(),
        method: newMethod.trim(),
        class: newClass.trim(),
        version,
        clockId: newClockId.trim() || null,
      },
      {
        onSuccess: () => {
          setNewErrorMessage("");
          setNewMethod("");
          setNewClass("");
          setNewClockId("");
        },
      },
    );
  };

  const rowBuilder = (row: IgnoredClockError) => (
    <>
      <DSTableCell sx={{ minWidth: 300, maxWidth: 500 }}>
        <DSCollapsibleText text={row.errorMessage} maxLines={2} />
      </DSTableCell>
      <DSTableCell>{row.method || "-"}</DSTableCell>
      <DSTableCell>{row.class || "-"}</DSTableCell>
      <DSTableCell>
        {row.clockId ? (
          row.clockId
        ) : (
          <DSChip label="Global" size="small" color="default" />
        )}
      </DSTableCell>
      <DSTableCell>{row.createdBy || "-"}</DSTableCell>
      <DSTableCell>
        {row.createdAt ? new Date(row.createdAt).toLocaleString() : "-"}
      </DSTableCell>
      <DSTableCell>
        {row.deletedAt ? (
          <DSBox sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <DSChip label="Inactive" size="small" color="default" />
            <DSIconButton
              size="small"
              onClick={() => restore(row._id)}
              title="Restore"
            >
              <RestoreFromTrashIcon color="primary" fontSize="small" />
            </DSIconButton>
          </DSBox>
        ) : (
          <DSBox sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <DSChip label="Active" size="small" color="success" />
            <DSIconButton
              size="small"
              onClick={() => softDelete(row._id)}
              title="Remove from ignore list"
            >
              <DeleteIcon color="error" fontSize="small" />
            </DSIconButton>
          </DSBox>
        )}
      </DSTableCell>
    </>
  );

  return (
    <DSBox sx={{ p: 3 }}>
      <DSBox sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
        <DSTypography variant="h6">Ignored Errors</DSTypography>
        <DSTextField
          label="Version"
          type="number"
          size="small"
          value={versionInput}
          onChange={(e) => setVersionInput(e.target.value)}
          onBlur={handleVersionChange}
          onKeyDown={(e) => {
            if (e.key === "Enter") handleVersionChange();
          }}
          sx={{ width: 120 }}
        />
        {version != null && (
          <DSTypography variant="body2" color="text.secondary">
            Showing ignored errors for version {version}
          </DSTypography>
        )}
      </DSBox>

      {createError && (
        <DSAlert severity="error" onClose={resetCreateError} sx={{ mb: 2 }}>
          {(createError as Error).message || "Failed to create ignored error"}
        </DSAlert>
      )}

      <DSBox
        sx={{
          display: "flex",
          gap: 2,
          mb: 3,
          alignItems: "flex-end",
          flexWrap: "wrap",
        }}
      >
        <DSTextField
          label="Error Message"
          size="small"
          value={newErrorMessage}
          onChange={(e) => setNewErrorMessage(e.target.value)}
          sx={{ minWidth: 300, flex: 1 }}
        />
        <DSTextField
          label="Method"
          size="small"
          value={newMethod}
          onChange={(e) => setNewMethod(e.target.value)}
          sx={{ width: 150 }}
        />
        <DSTextField
          label="Class"
          size="small"
          value={newClass}
          onChange={(e) => setNewClass(e.target.value)}
          sx={{ width: 150 }}
        />
        <DSTextField
          label="Clock ID (optional)"
          size="small"
          value={newClockId}
          onChange={(e) => setNewClockId(e.target.value)}
          helperText="Leave blank to ignore for all clocks"
          sx={{ width: 220 }}
        />
        <DSButton
          variant="contained"
          onClick={handleCreate}
          disabled={isCreating || !newErrorMessage.trim() || !version}
        >
          {isCreating ? "Adding..." : "Add"}
        </DSButton>
      </DSBox>

      {isLoading ? (
        <DSBox sx={{ display: "flex", justifyContent: "center", p: 4 }}>
          <DSCircularProgress />
        </DSBox>
      ) : (
        <DSBaseTable
          data={data ?? []}
          columns={columns}
          rowBuilder={rowBuilder}
          getRowId={(row) => row._id}
          defaultRowsPerPage={25}
        />
      )}
    </DSBox>
  );
};

export default IgnoredErrorsTab;
